// Epoch 17: Main Leads Inbox Page Component
// Updated Epoch 26: Added grey overlay for expired trial users
// Date: July 25, 2025 / Updated: August 7, 2025

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthSession } from '../../hooks/useAuthSession';
import { useTrialContext } from '../../hooks/useTrialContext';
import { LeadResponse } from '../../types/lead-responses';
import { supabase } from '../../supabase/client';
// Header is provided by AuthenticatedRoute wrapper
import LeadsInboxList from './LeadsInboxList';
import LeadResponseDetail from './LeadResponseDetail';
// EPOCH 29: Removed CSRLeadResponseForm - using navigation to form page instead

const LeadsInbox: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { session } = useAuthSession();
  const trial = useTrialContext();
  const user = session?.user;
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  // EPOCH 29: Removed showCreateForm - using navigation instead
  const [refreshKey, setRefreshKey] = useState(0);

  // Success message state
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Epoch 30: Lead response counters
  const [newResponsesCount, setNewResponsesCount] = useState(0);
  const [pendingRepliesCount, setPendingRepliesCount] = useState(0);
  const [companyId, setCompanyId] = useState<string | null>(null);

  const userRole = user?.user_metadata?.role || 'user';
  const canCreateLeadResponse = ['csr', 'admin', 'super_admin'].includes(userRole);

  // Check for success message from navigation
  useEffect(() => {
    const state = location.state as { message?: string; selectedLeadId?: string } | null;
    if (state?.message) {
      setSuccessMessage(state.message);

      // If there's a selected lead, set it
      if (state.selectedLeadId) {
        setSelectedLeadId(state.selectedLeadId);
        setRefreshKey(prev => prev + 1); // Trigger list refresh
      }

      // Clear navigation state to prevent message on refresh (delayed to avoid race condition)
      setTimeout(() => {
        navigate(location.pathname, { replace: true, state: {} });
      }, 100);

      // Auto-dismiss after 5 seconds
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [location.state, location.pathname, navigate]);

  // Load user's company ID and response counts
  useEffect(() => {
    const loadUserCompany = async () => {
      if (!session?.user?.id) return;

      const { data: userData } = await supabase
        .from('users')
        .select('company_id')
        .eq('id', session.user.id)
        .single();

      if (userData?.company_id) {
        setCompanyId(userData.company_id);
      }
    };

    loadUserCompany();
  }, [session]);
  
  useEffect(() => {
    if (!companyId || !session?.user?.id) return;
    
    const loadCounts = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('lead-response-counts', {
          body: { 
            user_id: session.user.id,
            company_id: companyId,
            user_role: userRole
          }
        });
        
        if (data && !error) {
          setNewResponsesCount(data.counts?.new_responses || 0);
          setPendingRepliesCount(data.counts?.pending_replies || 0);
        }
      } catch (error) {
        console.error('Error loading counts:', error);
      }
    };
    
    // Load counts initially
    loadCounts();
    
    // Refresh every 30 seconds
    const interval = setInterval(loadCounts, 30000);
    
    return () => clearInterval(interval);
  }, [companyId, session, userRole, refreshKey]); // Refresh on refreshKey change

  const handleLeadSelect = (leadResponse: LeadResponse) => {
    setSelectedLeadId(leadResponse.id);
  };

  const handleCloseDetail = () => {
    setSelectedLeadId(null);
  };

  // EPOCH 29: Removed handleCreateSuccess - handled by navigation return

  const handleReplySuccess = () => {
    setRefreshKey(prev => prev + 1); // Trigger refresh of the list
  };

  // Mobile: Stack views, Desktop: Side-by-side
  const isMobile = window.innerWidth < 1024;

  return (
    <div className="min-h-screen bg-gray-50">
      
      {/* Page Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Lead Responses Inbox</h1>
              <p className="text-sm text-gray-600 mt-1">
                {userRole === 'csr' 
                  ? 'Submit and manage LinkedIn lead responses'
                  : 'Review lead responses and request replies'}
              </p>
            </div>
            
            {canCreateLeadResponse && (
              <div className="flex space-x-3">
                {/* Epoch 34: Updated button labels for clarity */}
                <button
                  onClick={() => {
                    // Navigate to form page for follow-up response
                    navigate('/leads-inbox/add?mode=followup');
                  }}
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Follow-up Response
                </button>
                <button
                  onClick={() => {
                    // EPOCH 29: Navigate to form page for new response
                    navigate('/leads-inbox/add?mode=new');
                  }}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add New Lead Response
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Success Message Banner */}
      {successMessage && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between shadow-sm">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-green-800 font-medium">{successMessage}</span>
            </div>
            <button
              onClick={() => setSuccessMessage(null)}
              className="text-green-600 hover:text-green-800 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Main Content with Trial Expired Overlay */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Mobile Layout: Stack views */}
        {isMobile ? (
          <div className={`space-y-6 ${trial.isTrialExpired || trial.isPaidLapsed || (!trial.isLoading && trial.isFreeUser) ? 'pointer-events-none' : ''}`}>
            {/* EPOCH 29: Removed inline form - using navigation instead */}
            {selectedLeadId ? (
              <div>
                <div className="mb-4">
                  <button
                    onClick={handleCloseDetail}
                    className="inline-flex items-center text-blue-600 hover:text-blue-700"
                  >
                    <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Inbox
                  </button>
                </div>
                <LeadResponseDetail
                  leadResponseId={selectedLeadId}
                  onClose={handleCloseDetail}
                  onReplySuccess={handleReplySuccess}
                />
              </div>
            ) : (
              <LeadsInboxList
                key={refreshKey}
                onLeadSelect={handleLeadSelect}
                selectedLeadId={selectedLeadId}
                showFilters={true}
                compact={false}
              />
            )}
          </div>
        ) : (
          /* Desktop Layout: Side-by-side */
          <div className={`grid lg:grid-cols-12 gap-6 ${trial.isTrialExpired || trial.isPaidLapsed || (!trial.isLoading && trial.isFreeUser) ? 'pointer-events-none' : ''}`}>
            {/* Left Panel: List */}
            <div className="lg:col-span-5">
              <LeadsInboxList
                key={refreshKey}
                onLeadSelect={handleLeadSelect}
                selectedLeadId={selectedLeadId}
                showFilters={true}
                compact={false}
              />
            </div>

            {/* Right Panel: Detail View */}
            <div className="lg:col-span-7">
              {selectedLeadId ? (
                <LeadResponseDetail
                  leadResponseId={selectedLeadId}
                  onClose={handleCloseDetail}
                  onReplySuccess={handleReplySuccess}
                />
              ) : (
                <div className="bg-white rounded-lg shadow-md p-12 text-center">
                  <div className="max-w-md mx-auto">
                    <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {userRole === 'csr' 
                        ? 'Submit Lead Responses' 
                        : 'Select a Lead Response'}
                    </h3>
                    <p className="text-gray-600 mb-6">
                      {userRole === 'csr' 
                        ? 'Click "Add Lead Response" to submit new LinkedIn lead responses from your outreach campaigns.'
                        : 'Choose a lead response from the list to view details and request replies.'}
                    </p>
                    
                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 gap-4 mt-8">
                      <div className="bg-blue-50 rounded-lg p-4">
                        <div className="text-2xl font-bold text-blue-600">{newResponsesCount}</div>
                        <div className="text-sm text-blue-700">New Responses</div>
                      </div>
                      <div className="bg-yellow-50 rounded-lg p-4">
                        <div className="text-2xl font-bold text-yellow-600">{pendingRepliesCount}</div>
                        <div className="text-sm text-yellow-700">Pending Replies</div>
                      </div>
                    </div>
                    
                    {canCreateLeadResponse && (
                      <button
                        onClick={() => {
                          // EPOCH 29: Navigate to form page
                          navigate('/leads-inbox/add?mode=new');
                        }}
                        className="mt-6 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Lead Response
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Grey Overlay for Free Users (signed up but not subscribed) */}
        {!trial.isLoading && trial.isFreeUser && (
          <div className="absolute inset-0 z-10 flex items-center justify-center">
            <div className="absolute inset-0 bg-gray-500 bg-opacity-50"></div>
            <div className="relative bg-white p-8 rounded-xl shadow-2xl max-w-md mx-4 text-center">
              <div className="mb-4">
                <svg className="w-16 h-16 text-blue-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Activate your account to access Leads Inbox
              </h3>
              <p className="text-gray-600 mb-6">
                Get dashboard access for $30/month to unlock your Leads Inbox and start tracking LinkedIn responses.
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/pricing')}
                  className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  Get Dashboard Access — $30/month
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
                >
                  Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Grey Overlay for Expired Trial Users (also covers lapsed offline-paid accounts) */}
        {(trial.isTrialExpired || trial.isPaidLapsed) && (
          <div className="absolute inset-0 z-10 flex items-center justify-center">
            {/* Semi-transparent overlay */}
            <div className="absolute inset-0 bg-gray-500 bg-opacity-50"></div>
            
            {/* Upgrade prompt card */}
            <div className="relative bg-white p-8 rounded-xl shadow-2xl max-w-md mx-4 text-center">
              <div className="mb-4">
                <svg className="w-16 h-16 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-.833-1.962-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                {trial.isPaidLapsed ? 'Subscription Expired' : 'Trial Expired'}
              </h3>

              <p className="text-gray-600 mb-6">
                {trial.isPaidLapsed
                  ? 'Your subscription has lapsed. Renew to continue accessing your lead responses and managing conversations.'
                  : 'Your trial has expired. Upgrade to a paid plan to continue accessing your lead responses and managing conversations.'}
              </p>
              
              <div className="space-y-3">
                <button 
                  onClick={() => navigate('/pricing')}
                  className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  View Plans & Upgrade
                </button>
                
                <button 
                  onClick={() => navigate('/dashboard')}
                  className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
                >
                  Back to Dashboard
                </button>
              </div>
              
              <p className="text-sm text-gray-500 mt-6">
                You can still use the Lead Generator with pay-per-export pricing
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadsInbox;